/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import LoadingContent from '../../components/LoadingContent';
import wpGeodirServices from '../../services/wpGeodirServices';
import userProgramsService from '../../services/userProgramsService';
import { useHistory } from 'react-router-dom';
import { appRoutes } from '../../router/router';
import { useAppSelector } from '../../app/hooks';
import { STRIPE_PRICE_NAME } from '../../constants/common';
import toast from 'react-hot-toast';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LockIcon from '@mui/icons-material/Lock';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';

const ProgramsSavedPage = () => {
  const [loading, setLoading] = React.useState(false);
  const [programs, setPrograms] = React.useState<any[]>([]);
  const [savedCount, setSavedCount] = useState<number>(0);
  const [maxSaved, setMaxSaved] = useState<number>(5);
  const [removingPrograms, setRemovingPrograms] = React.useState<Set<number>>(new Set());
  const [addingToManagerIds, setAddingToManagerIds] = React.useState<Set<number>>(new Set());
  const [programIdsInManager, setProgramIdsInManager] = React.useState<Set<number>>(new Set());
  const [programsManagerMap, setProgramsManagerMap] = React.useState<Map<number, any>>(new Map());
  const [showUpgradeModal, setShowUpgradeModal] = React.useState<boolean>(false);
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc' | null>(null);
  const [showFilterPanel, setShowFilterPanel] = React.useState(false);
  const [filterSearch, setFilterSearch] = React.useState('');
  const [filterInstitution, setFilterInstitution] = React.useState('');
  const [filterDeadline, setFilterDeadline] = React.useState('');
  const history = useHistory();
  const stripeSubscriptionInfo = useAppSelector((store) => store.basicInformation.stripeSubscriptionInfo);
  const isTrial = !stripeSubscriptionInfo || stripeSubscriptionInfo?.subcription_plan === STRIPE_PRICE_NAME.GradMapStarter;

  // Load saved programs
  const loadSavedPrograms = async () => {
    try {
      setLoading(true);
      const response: any = await wpGeodirServices.getSavedPrograms();
      console.log('Saved programs response:', response);

      // Handle different response structures
      if (response) {
        // Case 1: response.data contains programs array
        if (response.data && Array.isArray(response.data)) {
          setPrograms(response.data);
          setSavedCount(response.data.length);
        }
        // Case 2: response.data.programs contains programs array
        else if (response.data && response.data.programs) {
          setPrograms(response.data.programs || []);
          setSavedCount(response.data.saved_count || response.data.programs.length || 0);
          setMaxSaved(response.data.max_saved || 5);
        }
        // Case 3: response is directly an array
        else if (Array.isArray(response)) {
          setPrograms(response);
          setSavedCount(response.length);
        }
        // Case 4: response.data is object with programs property
        else if (response.data && typeof response.data === 'object') {
          setPrograms(response.data.programs || []);
          setSavedCount(response.data.saved_count || response.data.count || 0);
          setMaxSaved(response.data.max_saved || response.data.max_count || 5);
        }
        else {
          setPrograms([]);
          setSavedCount(0);
        }
      } else {
        setPrograms([]);
        setSavedCount(0);
      }
      setLoading(false);

      console.log('maxSaved: ',maxSaved);
      console.log('savedCount: ',savedCount); 

    } catch (error: any) {
      setLoading(false);
      console.error('Error loading saved programs:', error);
      toast.error('Failed to load saved programs');
    }
  };

  const loadProgramsManagerIds = async () => {
    try {
      const res: any = await userProgramsService.getProgramsManagerList();
      const arr = Array.isArray(res) ? res : (res?.data ?? []);
      const ids = new Set<number>(arr.map((row: any) => row.program_id).filter((id: any) => id != null));
      const map = new Map<number, any>();
      arr.forEach((row: any) => {
        const pid = row.program_id ?? row.program?.id;
        if (pid != null) map.set(Number(pid), row);
      });
      setProgramIdsInManager(ids);
      setProgramsManagerMap(map);
    } catch {
      setProgramIdsInManager(new Set());
      setProgramsManagerMap(new Map());
    }
  };

  useEffect(() => {
    loadSavedPrograms();
  }, []);

  useEffect(() => {
    if (programs.length > 0) loadProgramsManagerIds();
  }, [programs.length]);

  const handleRemove = async (programId: number) => {
    if (removingPrograms.has(programId)) {
      return;
    }

    try {
      setRemovingPrograms(prev => new Set(prev).add(programId));
      await wpGeodirServices.unsaveProgram(programId);
      // Remove from list
      setPrograms(prev => prev.filter(p => p.id !== programId));
      setSavedCount(prev => Math.max(0, prev - 1));
      toast.success('Program removed from saved list');
    } catch (error: any) {
      console.error('Error removing program:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to remove program';
      toast.error(errorMessage);
    } finally {
      setRemovingPrograms(prev => {
        const newSet = new Set(prev);
        newSet.delete(programId);
        return newSet;
      });
    }
  };

  const onClickDetail = (item: any) => {
    let search_query = `id=${item.id}`;
    history.push({
      pathname: appRoutes.summerProgramsDetail.path,
      search: search_query,
    });
  };

  const handleAddToProgramManager = async (item: any) => {
    const programId = item?.id;
    if (programId == null) return;
    const alreadyInManager = programIdsInManager.has(programId);
    if (alreadyInManager) {
      history.push(appRoutes.summerProgramsManager.path);
      return;
    }
    if (addingToManagerIds.has(programId)) return;
    setAddingToManagerIds((prev) => new Set(prev).add(programId));
    try {
      await userProgramsService.addToProgramsManager(programId);
      setProgramIdsInManager((prev) => new Set(prev).add(programId));
      toast.success('Program added to Program Manager');
      history.push({
        pathname: appRoutes.summerProgramsDetail.path,
        search: `id=${programId}`,
      });
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to add program to Program Manager';
      toast.error(message);
    } finally {
      setAddingToManagerIds((prev) => {
        const next = new Set(prev);
        next.delete(programId);
        return next;
      });
    }
  };

  const parseDateFlexible = (s: string | null | undefined): Date | null => {
    if (!s || typeof s !== 'string') return null;
    const trimmed = s.trim();
    if (!trimmed) return null;
    let d = new Date(trimmed);
    if (!isNaN(d.getTime())) return d;
    if (trimmed.includes('/')) {
      const parts = trimmed.split('/');
      if (parts.length === 3) {
        const month = parseInt(parts[0], 10) - 1;
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
          d = new Date(year, month, day);
          if (!isNaN(d.getTime())) return d;
        }
      }
    }
    return null;
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString || !dateString.trim()) return 'Rolling Admissions';
    const date = parseDateFlexible(dateString);
    if (!date) return 'Rolling Admissions';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  /** Get raw deadline string: prefer programs manager (targetDate) when available, else program fields */
  const getDeadlineRaw = (item: any): string | null => {
    const programId = item?.id ?? item?.program_id;
    const managerRow = programId != null ? programsManagerMap.get(Number(programId)) : null;
    if (managerRow) {
      const program = managerRow.program || {};
      const appForm = managerRow.application_form;
      const s =
        (appForm?.applicationDeadline?.trim() || null) ??
        program?.applicationDeadline ??
        program?.app_deadline ??
        managerRow.applicationDeadline ??
        managerRow.application_deadline ??
        null;
      return s || null;
    }
    const raw =
      (item?.applicationDeadline ?? item?.app_deadline) ||
      item?.application_deadline ||
      item?.application_due ||
      item?.deadline;
    return raw && String(raw).trim() ? String(raw).trim() : null;
  };

  const getApplicationDeadline = (item: any): string => {
    return formatDate(getDeadlineRaw(item));
  };

  const getProgramName = (item: any): string => {
    return (item?.programName ?? item?.program_name) || item?.post_title || item?.title || 'Untitled Program';
  };

  const getInstitutionName = (item: any): string => {
    return item?.institution || item?.post_author_name || item?.school_name || 'Unknown Institution';
  };

  const getDeadlineDate = (item: any): Date | null => {
    const raw = getDeadlineRaw(item);
    return raw ? parseDateFlexible(raw) : null;
  };

  const institutionOptions = React.useMemo(() => {
    const names = new Set<string>();
    programs.forEach((p) => {
      const name = getInstitutionName(p);
      if (name && name !== 'Unknown Institution') names.add(name);
    });
    return [{ value: '', name: 'All Institutions' }, ...Array.from(names).sort().map((n) => ({ value: n, name: n }))];
  }, [programs]);

  const deadlineOptions = [
    { value: '', name: 'All Deadlines' },
    { value: 'upcoming', name: 'Upcoming (next 90 days)' },
    { value: 'this_month', name: 'This month' },
    { value: 'past', name: 'Past' },
    { value: 'rolling', name: 'Rolling / No deadline' },
  ];

  const filteredAndSortedPrograms = React.useMemo(() => {
    let result = programs;

    if (filterSearch.trim()) {
      const q = filterSearch.trim().toLowerCase();
      result = result.filter((p) => {
        const name = getProgramName(p).toLowerCase();
        const inst = getInstitutionName(p).toLowerCase();
        return name.includes(q) || inst.includes(q);
      });
    }

    if (filterInstitution) {
      result = result.filter((p) => getInstitutionName(p) === filterInstitution);
    }

    if (filterDeadline) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const in90Days = new Date(today);
      in90Days.setDate(in90Days.getDate() + 90);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      result = result.filter((p) => {
        const d = getDeadlineDate(p);
        if (filterDeadline === 'rolling') return d === null;
        if (d === null) return false;
        const date = new Date(d);
        date.setHours(0, 0, 0, 0);
        if (filterDeadline === 'upcoming') return date >= today && date <= in90Days;
        if (filterDeadline === 'this_month') return date >= today && date <= endOfMonth;
        if (filterDeadline === 'past') return date < today;
        return true;
      });
    }

    if (sortOrder) {
      result = [...result].sort((a, b) => {
        const nameA = getProgramName(a).toLowerCase();
        const nameB = getProgramName(b).toLowerCase();
        const cmp = nameA.localeCompare(nameB);
        return sortOrder === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [programs, filterSearch, filterInstitution, filterDeadline, sortOrder, programsManagerMap]);

  const hasActiveFilters = !!(filterSearch.trim() || filterInstitution || filterDeadline);

  const clearFilters = () => {
    setFilterSearch('');
    setFilterInstitution('');
    setFilterDeadline('');
  };

  const handleSortClick = () => {
    setSortOrder((prev) => {
      if (prev === null) return 'asc';
      if (prev === 'asc') return 'desc';
      return null;
    });
  };

  return (
    <Container
      style={{
        width: '100%',
        maxWidth: '100%',
        padding: '1.25rem',
        margin: '0 auto',
      }}
    >
      <div style={{ width: '100%', maxWidth: '75rem', margin: '0 auto' }}>
        {/* Page Title and Subtitle */}
        <div className="row normal pt-4 pb-2">
          <div className="col-md-12">
            <h1 style={{
              fontSize: '28px',
              fontWeight: 700,
              color: '#111827',
              margin: '0 0 8px 0',
              lineHeight: 1.2
            }}>
              Saved Programs
            </h1>
            {
              isTrial && (
                <p style={{
                  fontSize: '15px',
                  color: '#6b7280',
                  margin: 0,
                  lineHeight: 1.5
                }}>
                  Upgrade to Silver+ to get full management access.
                </p>
              )
            }
          </div>
        </div>

        {/* Upgrade to Silver+ Banner */}
        {isTrial && (
          <div className="row normal py-3">
            <div className="col-md-12">
              <div
                style={{
                  backgroundColor: '#eff6ff',
                  borderRadius: '12px',
                  border: '1px solid #bfdbfe',
                  padding: '20px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '24px',
                  flexWrap: 'wrap'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}
                  >
                    <RocketLaunchIcon style={{ color: '#2563eb', fontSize: '28px' }} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontSize: '17px',
                      fontWeight: 600,
                      color: '#111827',
                      marginBottom: '4px',
                      lineHeight: 1.4
                    }}>
                      Ready to take the next step? Upgrade to Silver+
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#374151',
                      lineHeight: 1.5
                    }}>
                      Track application deadlines, manage requirements, unlimited saved programs and get personalized insights.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn"
                  style={{
                    backgroundColor: '#2563eb',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                  onClick={() => history.push(appRoutes.upgradeToSilverPlus.path)}
                >
                  Upgrade to Silver+
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Your Saved Programs - single card: header + list */}
        <div className="row normal py-3">
          <div className="col-md-12">
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
                overflow: 'hidden'
              }}
            >
              {/* Header row inside card */}
              <div
                style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <h2 style={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#111827',
                    margin: 0
                  }}>
                    Your Saved Programs
                  </h2>
                  {isTrial && (
                    <button
                      type="button"
                      onClick={() => history.push(appRoutes.upgradeToSilverPlus.path)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      <LockIcon sx={{ fontSize: '16px' }} />
                      <span>Upgrade for Unlimited Saves</span>
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setShowFilterPanel((p) => !p)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: hasActiveFilters ? '#2563eb' : '#6b7280',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      padding: '4px 8px'
                    }}
                  >
                    <ArrowDropDownIcon sx={{ fontSize: '20px', transform: showFilterPanel ? 'rotate(180deg)' : 'none' }} />
                    <span>Filter{hasActiveFilters ? ' (on)' : ''}</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSortClick}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: sortOrder ? '#2563eb' : '#6b7280',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      padding: '4px 8px'
                    }}
                  >
                    <SwapVertIcon style={{ fontSize: '18px' }} />
                    <span>Sort{sortOrder ? ` (${sortOrder === 'asc' ? 'A-Z' : 'Z-A'})` : ''}</span>
                  </button>
                </div>
              </div>

              {/* Filter panel */}
              {showFilterPanel && programs.length > 0 && (
                <div
                  style={{
                    padding: '16px 24px',
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '16px',
                    alignItems: 'center'
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search by program or institution..."
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: '#9ca3af', fontSize: '20px' }} />
                        </InputAdornment>
                      ),
                      style: { height: '40px', borderRadius: '8px', backgroundColor: '#fff' }
                    }}
                    sx={{
                      minWidth: '220px',
                      flex: '1 1 200px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb', borderRadius: '8px' }
                    }}
                  />
                  <select
                    value={filterInstitution}
                    onChange={(e) => setFilterInstitution(e.target.value)}
                    style={{
                      height: '40px',
                      minWidth: '180px',
                      padding: '0 12px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#fff',
                      fontSize: '14px',
                      color: '#374151'
                    }}
                  >
                    {institutionOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.name}</option>
                    ))}
                  </select>
                  <select
                    value={filterDeadline}
                    onChange={(e) => setFilterDeadline(e.target.value)}
                    style={{
                      height: '40px',
                      minWidth: '180px',
                      padding: '0 12px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#fff',
                      fontSize: '14px',
                      color: '#374151'
                    }}
                  >
                    {deadlineOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.name}</option>
                    ))}
                  </select>
                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      style={{
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#6b7280',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )}

              {/* List content inside same card */}
              {loading ? (
                <div style={{ padding: '32px 24px' }}>
                  <LoadingContent />
                </div>
              ) : programs.length > 0 ? (
                <>
                  {filteredAndSortedPrograms.length === 0 ? (
                    <div style={{ padding: '32px 24px', textAlign: 'center' }}>
                      <p style={{ fontSize: '16px', color: '#6b7280', margin: 0 }}>No programs match your filters.</p>
                      <button
                        type="button"
                        onClick={clearFilters}
                        style={{
                          marginTop: '12px',
                          padding: '8px 16px',
                          fontSize: '14px',
                          color: '#2563eb',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                      >
                        Clear filters
                      </button>
                    </div>
                  ) : (
                  filteredAndSortedPrograms.map((item, index) => {
                  const isRemoving = removingPrograms.has(item.id);
                  const programName = getProgramName(item);
                  const institutionName = getInstitutionName(item);
                  const deadline = getApplicationDeadline(item);

                  return (
                    <div
                      key={`saved_program_${item.id}`}
                      style={{
                        padding: '20px 24px',
                        borderBottom: index < filteredAndSortedPrograms.length - 1 ? '1px solid #e5e7eb' : 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '24px'
                      }}
                    >
                      {/* Left: Program Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: '700',
                          color: '#111827',
                          margin: '0 0 8px 0',
                          lineHeight: '1.4'
                        }}>
                          {programName}
                        </h3>
                        <p style={{
                          fontSize: '16px',
                          color: '#6b7280',
                          margin: '0 0 8px 0',
                          lineHeight: '1.5'
                        }}>
                          {institutionName}
                        </p>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          color: '#6b7280'
                        }}>
                          <CalendarTodayIcon style={{ fontSize: '16px', color: '#9ca3af' }} />
                          <span>Application Deadline: {deadline}</span>
                        </div>
                      </div>

                      {/* Right: View Program, Add to Program Manager, Upgrade to Manage (trial), Delete */}
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'center',
                        flexShrink: 0
                      }}>
                        <button
                          type="button"
                          onClick={() => onClickDetail(item)}
                          disabled={loading}
                          style={{
                            backgroundColor: '#fff',
                            color: '#111827',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: 500,
                            cursor: loading ? 'wait' : 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          View Program
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAddToProgramManager(item)}
                          disabled={addingToManagerIds.has(item.id)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: '#2563eb',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '10px 16px',
                            fontSize: '14px',
                            fontWeight: 500,
                            cursor: addingToManagerIds.has(item.id) ? 'wait' : 'pointer',
                            whiteSpace: 'nowrap',
                            opacity: addingToManagerIds.has(item.id) ? 0.7 : 1
                          }}
                        >
                          {programIdsInManager.has(item.id) ? (
                            <VisibilityIcon sx={{ fontSize: '18px' }} />
                          ) : (
                            <AddIcon sx={{ fontSize: '18px' }} />
                          )}
                          <span>
                            {addingToManagerIds.has(item.id)
                              ? 'Adding...'
                              : programIdsInManager.has(item.id)
                                ? 'View Program Manager'
                                : 'Add to Program Manager'}
                          </span>
                        </button>
                        {isTrial ? (
                          <button
                            type="button"
                            onClick={() => history.push(appRoutes.upgradeToSilverPlus.path)}
                            style={{
                              backgroundColor: '#2563eb',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '10px 20px',
                              fontSize: '14px',
                              fontWeight: 500,
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Upgrade to Manage
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => handleRemove(item.id)}
                          disabled={isRemoving}
                          aria-label="Remove from saved"
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: '#dc2626',
                            padding: '8px',
                            cursor: isRemoving ? 'wait' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: isRemoving ? 0.6 : 1
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: '22px' }} />
                        </button>
                      </div>
                    </div>
                  );
                  })
                  )}
                </>
              ) : (
                <div style={{ padding: '32px 24px', textAlign: 'center' }}>
                  <p style={{ fontSize: '16px', color: '#6b7280', margin: 0 }}>No saved programs yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>

            {/* What You'll Unlock Section - Only show for trial users at limit */}
            {isTrial && programs.length > 0 && (
              <div className="row normal py-4 mt-4">
                <div className="col-md-12">
                  <div
                    style={{
                      backgroundColor: '#f3f4f6',
                      borderRadius: '16px',
                      padding: '32px',
                      marginTop: '32px'
                    }}
                  >
                    <h3
                      style={{
                        fontSize: '24px',
                        fontWeight: '700',
                        color: '#111827',
                        marginBottom: '24px'
                      }}
                    >
                      What You'll Unlock with Silver+
                    </h3>
                    <div className="row">
                      <div className="col-md-6">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {[
                            'Unlimited saved programs',
                            'Application requirement tracking',
                            'Essay & recommendation management'
                          ].map((feature, index) => (
                            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <CheckCircleIcon style={{ color: '#9D7AD8', fontSize: '20px', flexShrink: 0 }} />
                              <span style={{ fontSize: '16px', color: '#374151' }}>{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {[
                            'Personal reminders',
                            'My Summer Plan dashboard'
                          ].map((feature, index) => (
                            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <CheckCircleIcon style={{ color: '#9D7AD8', fontSize: '20px', flexShrink: 0 }} />
                              <span style={{ fontSize: '16px', color: '#374151' }}>{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <button
                        className="btn"
                        style={{
                          backgroundColor: '#9D7AD8',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '12px 32px',
                          fontSize: '16px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          minWidth: '200px'
                        }}
                        onClick={() => setShowUpgradeModal(true)}
                      >
                        Upgrade to Silver+
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
      </div>

      {/* Upgrade Modal */}
      <Dialog
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 24px 8px 24px',
            borderBottom: 'none'
          }}
        >
          <Box>
            <Box
              sx={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#111827',
                marginBottom: '8px'
              }}
            >
              Upgrade to Silver+
            </Box>
            <Box
              sx={{
                fontSize: '16px',
                color: '#374151',
                fontWeight: '400'
              }}
            >
              Unlock full program management for your summer applications.
            </Box>
          </Box>
          {/* <IconButton
            onClick={() => setShowUpgradeModal(false)}
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            <CloseIcon />
          </IconButton> */}
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          {/* Feature Comparison */}
          <Box sx={{ display: 'flex', gap: '32px', marginBottom: '32px' }}>
            {/* Starter Column */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#111827',
                  marginBottom: '16px',
                }}
              >
                Starter
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  'Save up to 5 programs',
                  'View program details',
                  'Limited browsing'
                ].map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CloseIcon sx={{ color: '#ef4444', fontSize: '20px', flexShrink: 0 }} />
                    <Box sx={{ fontSize: '15px', color: '#374151', fontWeight: 'bold' }}>{feature}</Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Silver+ Column */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#7e22ce',
                  marginBottom: '16px'
                }}
              >
                Silver+
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  'Unlimited programs',
                  'Application tracking',
                  'Essay & recommendation manager',
                  'Reminders & deadlines',
                  'My Summer Plan dashboard'
                ].map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircleIcon sx={{ color: '#7e22ce', fontSize: '20px', flexShrink: 0 }} />
                    <Box sx={{ fontSize: '15px', color: '#7e22ce', fontWeight: 'bold' }}>{feature}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            <button
              className="btn"
              style={{
                flex: 1,
                backgroundColor: '#9D7AD8',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                height: '48px'
              }}
              onClick={() => {
                setShowUpgradeModal(false);
                history.push('/payment');
              }}
            >
              Upgrade to Silver+
            </button>
            <button
              className="btn"
              style={{
                backgroundColor: '#fff',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                height: '48px',
                minWidth: '120px'
              }}
              onClick={() => setShowUpgradeModal(false)}
            >
              Not Now
            </button>
          </Box>

          {/* Additional Information */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb',
              alignItems: 'center',
            }}
          >
            {[
              'Cancel anytime',
              'Built by college counselors',
              'Designed for real application workflows'
            ].map((info, index) => (
              <Box
                key={index}
                sx={{
                  fontSize: '14px',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Box sx={{ fontSize: '12px' }}>•</Box>
                <Box>{info}</Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ProgramsSavedPage;
